import { useState, useEffect } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentTenantDetails, useUpdateCurrentTenant } from '@/hooks/use-tenants';

export function TenantSettingsPage() {
    const { data: tenant, isLoading } = useCurrentTenantDetails();
    const updateTenant = useUpdateCurrentTenant();

    const [name, setName] = useState('');

    useEffect(() => {
        if (tenant?.name) {
            setName(tenant.name);
        }
    }, [tenant?.name]);

    if (isLoading) {
        return <SettingsSkeleton />;
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error('Organization name is required');
            return;
        }

        updateTenant.mutate(
            { name: name.trim() },
            {
                onSuccess: () => {
                    toast.success('Organization updated successfully');
                },
                onError: () => {
                    toast.error('Failed to update organization');
                },
            }
        );
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Organization Settings</h1>
                <p className="text-muted-foreground">Manage your organization settings</p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>General Information</CardTitle>
                    <CardDescription>Update your organization details</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Organization Name</Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Organization name"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="slug">URL Slug</Label>
                            <Input id="slug" value={tenant?.slug ?? ''} disabled />
                            <p className="text-xs text-muted-foreground">The URL slug cannot be changed</p>
                        </div>

                        <Button type="submit" disabled={updateTenant.isPending}>
                            {updateTenant.isPending ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Organization Details</CardTitle>
                    <CardDescription>Additional organization information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-muted-foreground">Organization ID</p>
                            <p className="font-mono">{tenant?.id}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground">Region</p>
                            <p>{tenant?.region}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground">Created</p>
                            <p>{tenant?.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : '-'}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground">Last Updated</p>
                            <p>{tenant?.updatedAt ? new Date(tenant.updatedAt).toLocaleDateString() : '-'}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function SettingsSkeleton() {
    return (
        <div className="space-y-6">
            <div>
                <Skeleton className="h-8 w-56" />
                <Skeleton className="mt-2 h-4 w-72" />
            </div>
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-4 w-56" />
                </CardHeader>
                <CardContent className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-24" />
                </CardContent>
            </Card>
        </div>
    );
}
